import React, { useEffect, useState, useRef } from 'react'
import stylesheet from '../detail-container.stylesheet'
import {
  Box, Button, Card, CardContent, Grid, Typography, CircularProgress, List, ListItem, Tooltip, IconButton,
  Stack
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import { useSelector } from 'react-redux'
import BlockIcon from '@mui/icons-material/Block';
import { RootState, useAppThunkDispatch } from '../../../../redux/store'
import CardLayoutComponent from '../common/card-layout.component'
import NotesComponent from '../common/notes.component'
import DeleteMedComponent from '../common/delete-med.component'
import moment from 'moment'
import { PatientService } from '../../../../services/patient.service'
import { useCheckConfirmCount } from '../../../../utils/use-checkconfirmationcount-hook'
import { useDispatchAPIs } from '../../../../utils/use-dispatchApis-hook'
import ReferralComponent from '../common/referral-card.component'
import { useReferralAction } from '../../../../utils/referral-hook'
import ScheduleCarePlanComponent from './schedule-care-plan.component'
import BaseDialogModalComponent from '../common/base-dialog-modal.component'
import { useCurrentUser } from '../../../../utils/use-current-user'
import { compsConfig } from '../../../../utils/dynamic-comps'
import ProgramSubmedicationsComponent from './program-submedication.component'
import LongMenu from '../care-plan/recommendation.component'
import ProgramEvaluationDialogComponent from '../care-plan/program-evaluation-dialog.component'
import { setSelectedCarePlan } from '../../../../redux/common-data-slice'
import Helper from '../../../../utils/helper'
import OtherMedicationsComponent from './other-medications.component'
import { checkCondition } from '../common/condition-check'
import IngredientTooltip from '../common/ingredient-tooltip'
import MedDetailsComponent from '../common/med-details.component'
import { environment } from '../../../../environments/environment'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'
import EmptyCardComponent from '../common/empty-card.component'
import { toast } from 'react-toastify'
import { useThemeMode } from '../../../../contexts/ThemeContext'
import { adjustColorForDarkMode } from '../../../../utils/color-utils'
import { profiler } from '../../../../utils/profiler'

const getExpectedPendingCount = (patientDetail: any, program: string): number => {
  let count = 0

  for (const conditionList of Object.values(patientDetail?.conditions || {})) {
    for (const c of conditionList as any[]) {
      if (c.programs?.includes(program) && !c.overridden_programs?.includes(program) && (c.needs_update || c.needs_confirmation))
        count++
    }
  }

  const diag = patientDetail?.diagnosis || {}
  for (const dp of diag.diagnostic_predicates || []) {
    if (dp.programs?.includes(program) && !(dp.programs?.length === dp.overridden_programs?.length && dp.programs?.every((p: string) => dp.overridden_programs?.includes(p))) && dp.needs_update)
      count++
  }
  for (const dt of diag.diagnostic_tests || []) {
    if (dt.current_status === 'deferred') continue
    if (dt.programs?.includes(program) && dt.needs_update)
      count++
  }
  for (const dp of diag.diagnosis_plan || []) {
    if (dp.programs?.includes(program) && dp.needs_update)
      count++
  }

  for (const cp of patientDetail?.medications?.care_plans || []) {
    for (const med of cp.medications || []) {
      if (med.programs?.length === med.overridden_programs?.length && med.programs?.every((p: string) => med.overridden_programs?.includes(p)))
        continue
      if (med.programs?.includes(program) && (med.needs_confirmation || med.needs_update))
        count++
    }
  }

  for (const mon of patientDetail?.medications?.monitors || []) {
    if (mon.programs?.includes(program) && (mon.needs_confirmation || mon.needs_update))
      count++
  }

  const measurements = patientDetail?.measurements || {}
  let targetCounted = false
  for (const [key, vals] of Object.entries(measurements)) {
    if (key === 'program_targets') {
      for (const m of vals as any[]) {
        if (m.program === program && !m.overridden_programs?.includes(program) && (m.needs_confirmation || m.needs_update) && !targetCounted) {
          count++
          targetCounted = true
        }
      }
    } else {
      for (const m of vals as any[]) {
        if (m.informational) continue
        const skip = new Set([...(m.overridden_programs || []), ...(m.step_b_programs || [])])
        if (m.programs?.includes(program) && !skip.has(program) && (m.needs_confirmation || m.needs_update))
          count++
      }
    }
  }

  if (patientDetail?.needs_update) count++

  const errorData = patientDetail?.error_data || {}
  if ((errorData[program] || []).length > 0) count++

  return count
}

interface IProps {
  patientInfo: any
  classes: ClassNameMap
}

const ProgramMedicationsComponent: React.FC<IProps> = ({
  patientInfo,
  classes,
}) => {
  const { mode } = useThemeMode()
  const isDarkMode = mode === 'dark'
  const [, setState] = useState('')
  const [data, setData] = useState([] as any)
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const [selectedNotes, setSelectedNotes] = useState([] as number[])
  const patientService = new PatientService()
  const patientId: string = patientDetail?.id
  const [loading, setLoading] = useState(false)
  const [isCaptureBaseline, setIsCaptureBaseline] = useState({ status: false, id: '' })
  const [IsOrderPlaced, setIsOrderPlaced] = useState({ status: false, id: '' })
  const [instructions, setInstructions] = useState([] as any)
  const [currentOption, setCurrentOption] = useState('')
  const [openFhirJson, setOpenFhirJson] = useState(false)
  const [medMoreDetails, setMedMoreDetails] = useState({} as any)
  const [medTitle, setMedTitle] = useState('')
  const { currentRole } = useCurrentUser()
  const onBtnChange = (_input: string) => setCurrentOption(_input)
  const [, setCount] = useCheckConfirmCount(patientDetail.id)
  const { setProgramConfirmCount } = useDispatchAPIs(patientDetail.id)
  const uiResource = useSelector((state: RootState) => state.metaData.metadata)
  const selectedProgram = useSelector((state: RootState) => state.commonData.selectedProgram)
  const program_enrollments = useSelector((state: RootState) => state.patientDetail.patientDetail?.program_enrollments || [])
  const { onSetView, onDataSubmit } = useReferralAction()
  const [open, setOpen] = useState(false)
  const [configItem, setConfigItem] = useState({} as any)
  const [currentItem, setCurrentItem] = useState({} as any)
  const multiProgramConfirm = useSelector((state: RootState) => state.multiProgramConfirm)
  const programTargets = patientDetail?.measurements?.program_targets || []
  const selectedCarePlan = useSelector((state: RootState) => state.commonData.selectedCarePlan)
  const rejected = useSelector((state: RootState) => state.commonData.carePlanRejected)
  const dispatch = useAppThunkDispatch()
  const [selectedProgramObj, setSelectedProgramObj] = useState({} as any)
  const [programEvalDialog, setProgramEvalDialog] = useState<{
    open: boolean
    program: string
    programDisplayName: string
    carePlanId: number
  }>({ open: false, program: '', programDisplayName: '', carePlanId: 0 })
  const [lastEvalAction, setLastEvalAction] = useState<Record<string, string>>({})
  const { updatePatientData } = useUpdateDataAction(patientDetail.id)
  const stageChange = useSelector((state: RootState) => state.stageChange.loading)
  const patientDetailStatus: any = useSelector((state: RootState) => state.patientDetail.status)

  useEffect(() => {
    if (!patientInfo?.isActionItems)
      dispatch(setSelectedCarePlan({}))
  }, [])

  useEffect(() => {
    if (rejected)
      updatePatientData({ isMeasurement: true, isTreatments: true })
  }, [rejected])

  const renderCountRef = useRef(0)

  useEffect(() => {
    renderCountRef.current += 1
    profiler.start(`useEffect[patientDetail] #${renderCountRef.current}`)
    const stage = patientDetail?.current_stage_name ?? ''
    setState(stage)
    initialiseData()
    profiler.end(`useEffect[patientDetail] #${renderCountRef.current}`)
  }, [patientDetail])

  const handleDialogClose = () => {
    setOpen(false)
    dispatch(setSelectedCarePlan({}))
  }

  const initialiseData = () => {
    profiler.start('initialiseData - total')

    profiler.start('initialiseData - get care_plans')
    const temp = patientDetail?.medications?.care_plans
    profiler.end('initialiseData - get care_plans')

    profiler.start('initialiseData - filter/map/flat medications')
    let _data = temp
      .filter((f: any) => f.medications.length > 0)
      .map((e: { medications: any[]; program: any }) => {
        return {
          ...e,
          medications: e.medications.map((m) => {
            return { ...m, program: e.program, mode: 'view' }
          }),
        }
      })
      .map((c: { medications: any }) => c.medications)
      .flat()
    profiler.end('initialiseData - filter/map/flat medications')

    if (patientInfo?.isActionItems === true) {
      profiler.start('initialiseData - filter actionItems')
      _data = _data?.filter((i: { needs_update: any, needs_confirmation: any, program: any }) => (i.needs_confirmation === true || i.needs_update === true) && i.program === patientInfo?.actionProgram)
      profiler.end('initialiseData - filter actionItems')
    }

    profiler.start('initialiseData - setData')
    setData(_data)
    profiler.end('initialiseData - setData')

    profiler.start('initialiseData - setInstructions')
    setInstructions(
      temp.map((e: { program: any; instructions: any }) => {
        return { program: e.program, instructions: e.instructions }
      })
    )
    profiler.end('initialiseData - setInstructions')

    profiler.start('initialiseData - setSelectedProgramObj')
    setSelectedProgramObj(
      selectedProgram.map((e: any) => {
        const carePlanObj = patientDetail?.medications?.care_plans?.find((f: { program: any }) => f.program === e)
        return {
          program: e,
          isRef: false,
          care_plan_id: carePlanObj?.care_plan_id,
          is_referred: carePlanObj?.is_referred,
          referral_id: carePlanObj?.referral_id || null
        }
      })
    )
    profiler.end('initialiseData - setSelectedProgramObj')

    profiler.end('initialiseData - total')
    console.log(`[PROF] [INFO] Processed ${_data?.length || 0} medications from ${temp?.length || 0} care plans`)
  }

  const handleClose = (_id: any, _mode: any) => {
    setData(
      data.map((el: any) => (el.id === _id ? { ...el, mode: _mode } : el))
    )
  }

  const getPrgPendingCount = (p: any) => multiProgramConfirm.find((m: any) => m.program === p)?.pending || 0

  const formatRightHeader = (d: any) => {
    if (d.is_recommended) {
      const isAllCaptureBaselineCount = (data?.filter((i: any) => i.is_recommended
        && i.is_communicated
        && i.approval_status !== 'Approved'
        && i.is_ordered === false
        && ['data_intake', 'med_baseline'].includes(getStageString(i.programs[0]))
        && !(i.titration_status?.includes('discontinue') && i.needs_update)
      ) || [])?.length || 0

      const approvedCount = (data?.filter((i: any) => i.is_recommended
        && i.is_communicated
        && i.approval_status === 'Approved'
        && i.program === d.program
        && i.is_ordered === false
        && ['data_intake', 'med_baseline'].includes(getStageString(i.programs[0]))
        && !(i.titration_status?.includes('discontinue') && i.needs_update)
      ) || [])?.length || 0

      const prgPendingCount = getPrgPendingCount(d.programs[0])

      if (d.is_order_submitted) {
        return ''
      } else if (d.is_baselined === true && d.is_ordered === false) {
        return (
          <Button
            onClick={() => onBtnSubmitOrder(d.id)}
            disabled={d.is_ordered === true || patientInfo?.isActionItems || stageChange === true || patientDetailStatus === 'loading'}
            style={d.is_ordered === false ? { backgroundColor: 'var(--color-paper)', color: 'var(--color-primary)' } : { backgroundColor: 'var(--color-paper)', }}
            variant="contained"
            size="small"
          >
            {IsOrderPlaced?.status && IsOrderPlaced?.id === d.id
              ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                <small style={{ whiteSpace: 'nowrap', textTransform: 'none' }}>Order Placed</small>
              )}
          </Button>
        )
      }
      else if (d.is_communicated && d.approval_status === 'Approved'
        && d.is_ordered === false
        && ['data_intake', 'med_baseline'].includes(getStageString(d.programs[0]))
        && ((prgPendingCount === approvedCount && isAllCaptureBaselineCount === 0
        ))
      ) {
        return (
          <Button
            onClick={() => isCaptureBaseline?.status && isCaptureBaseline?.id === d.id ? null : onBtnCaptureBaseline(d.id, d)}
            disabled={isCaptureBaseline?.status || patientInfo?.isActionItems ||
              stageChange === true || patientDetailStatus === 'loading'
            }
            style={{ backgroundColor: 'var(--color-paper)', color: 'var(--color-primary)' }}
            variant="contained"
            size="small"
          >
            {isCaptureBaseline?.status && isCaptureBaseline?.id === d.id
              ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                <small style={{ whiteSpace: 'nowrap', textTransform: 'none' }}>Capture Baseline</small>
              )}

          </Button>
        )
      } else if (['med_evaluation', 'maintenance_surveillance', 'early_tox_check']
        .includes(program_enrollments.find((f: { program: any }) => f.program === d.programs[0])?.current_stage)) {
        return `${checkCondition(d?.evaluation_status !== null, d?.evaluation_status?.replaceAll('_', ' '), '')}`
      } else {
        return ''
      }
    } else {
      return (
        <Typography variant="body1">
          {' '}
          <strong>
            {d.intake_regularity.charAt(0).toUpperCase() +
              d.intake_regularity.slice(1)}
          </strong>
        </Typography>
      )
    }
  }

  const onBtnSubmitOrder = (_id: string) => {
    setIsOrderPlaced({ status: true, id: _id })
    updateMedication(_id.toString(), { order_date: moment(new Date()).unix(), })
      .then((res: any) => {
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        setIsOrderPlaced({ status: false, id: '' })
      }).catch((err: any) => {
        setIsOrderPlaced({ status: false, id: '' })
        toast.error(Helper.getErrorText(err), Helper.bottom_center())
      })
  }

  const onBtnCaptureBaseline = (_id: string, d: any) => {
    setIsCaptureBaseline({ status: true, id: _id })
    const eId = program_enrollments?.find((f: { program: any }) => f.program === d.programs[0])?.enrollment_id || ''
    eId !== '' && patientService.captureBaseline(eId).then((_res: any) => {
      setIsCaptureBaseline({ status: false, id: '' })
      window.location.reload()
      //   updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
    }).catch((err: any) => setIsCaptureBaseline({ status: false, id: '' }))
  }

  const formatMedDetails = (pu_rec: any[], dosage_info: any[]) => {
    return checkCondition(Object.keys(pu_rec).length > 0, getMedDetails(pu_rec, dosage_info), '')
  }

  const getMedDetails = (temp: any, dosage_info: any) => {
    const number = temp?.num_units || ''
    const formulation = temp?.formulation_display_name || ''
    const route = temp?.route_display_name || ''
    const frequency = uiResource.MEDICATION_DOSAGE_FREQUENCY?.[temp?.frequency] || ''
    // const directions = temp?.directions?.init || ''
    const dosageInfo = (dosage_info) && dosage_info?.[0]?.medicationTimings
      ? uiResource.MEDICATION_TIMINGS?.[dosage_info[0]['medicationTimings']]
      : ''
    const text = `${number}${formulation}${route}${frequency}` ? `${number} ${formulation} ${route} ${Helper.toLowerlCase(frequency)} ${dosageInfo}` : 'Details not available'
    return `${text}`
  }

  const formatSubHeader = (_d: any) => {
    const sub = checkCondition(_d.intake_started_on === null, 'Start', 'Taking')
    const titrate = checkCondition(_d.intake_started_on !== null && !_d.titration_status?.includes('discontinue'), 'Taking', 'Stop')

    const isDisAllowSub = _d?.disallow_substitution
      ? <Tooltip data-testid='disallow_substitution' title={'Disallow substitution true'} placement="bottom">
        <IconButton size='small' className={classes.infoIcon}>
          <BlockIcon className={classes.infoIconFont} sx={{ fill: '#e54a4ae8' }} />
        </IconButton>
      </Tooltip>
      : ''

    if (_d?.pu_order && _d.pu_order !== undefined && Object.keys(_d.pu_order).length > 0) {
      const initiateMed = getMedDetails(_d.pu_order, _d?.dosage_info)
      if (_d.titration_status === 'initiate_current') {
        const text = `continue ${getMedDetails(_d.pu_order, _d?.dosage_info)}`
        return <>{text}<IngredientTooltip ingredients={_d.calc_dose} />{isDisAllowSub}</>
      } else {
        const text = _d.titration_status === 'initiate'
          ? `${sub} ${initiateMed}`
          : `${titrate} ${getMedDetails(_d.pu_order, _d?.dosage_info)}`
        return <>{text}<IngredientTooltip ingredients={_d.calc_dose} />{isDisAllowSub}</>
      }
    } else {
      if (Object.keys(_d.pu_rec)?.length > 0 && _d.titration_status != 'initiate_current') {
        const puOrderMedDetails = getMedDetails(_d?.pu_order, _d?.dosage_info)
        const initiateMed = checkCondition(_d.intake_started_on === null, formatMedDetails(_d.pu_rec, _d?.dosage_info), puOrderMedDetails)
        const text = checkCondition(_d.titration_status === 'initiate', `${sub} ${initiateMed}`, `${titrate} ${puOrderMedDetails}`)
        return <>{text}<IngredientTooltip ingredients={_d.calc_dose} />{isDisAllowSub}</>
      } else {
        return 'Details not available'
      }
    }
  }

  const formatSubHeader1 = (d: any) => {
    if (Object.keys(d.pu_rec)?.length > 0) {
      if (d.titration_status != 'initiate_current') {
        const medDetails = checkCondition(d.intake_started_on === null && d.pu_rec !== undefined, formatMedDetails(d.pu_rec, d?.dosage_info), '')
        const text = checkCondition(d.intake_started_on === null && d.pu_rec !== undefined, `Start ${medDetails}`, '')
        return <>{text}</>
      }
    } else {
      return 'Details not available'
    }
  }

  const formatConfirmDate = (d: any) => Helper.dateChecking(d.recommendation_message) // d.recommendation_message

  const setIcons = (_d: any) => {
    return [
      { iconType: 'edit', callback: onSetEdit },
      { iconType: 'more', callback: {} },
    ]
  }

  const onNotesClick = (_id: any) => {
    (selectedNotes.includes(+_id))
      ? setSelectedNotes([...selectedNotes.filter((f) => f !== +_id)])
      : setSelectedNotes([...selectedNotes, +_id])
  }

  const getInnerProps = (t: any, _item: any) => {
    if (t.comp === 'ScheduleCarePlanComponent_Key') {
      return { ...t, props: { data: _item, outer: outerSubmit } }
    } else if (t.comp === 'ReferralComponent_Key') {
      return {
        ...t,
        props: {
          entityRef: 'medication',
          input: _item,
          patientId: patientId,
          leftHeader: Helper.getRXnorm(_item.name, _item?.rxnorm),
          rightHeader: formatRightHeader(_item),
          subHeader: formatSubHeader(_item),
          programs: _item.programs,
          closeRef: () => onRefClick(_item.id, 'view'),
          onRefSubmit: onRefSubmit,
        },
      }
    } else if (t.comp === 'NotesComponent_Key') {
      return {
        ...t,
        props: {
          entityId: _item.id,
          entityRef: 'medication',
          onCancel: onNotesClick,
        },
      }
    } else {
      return { ...t, props: {} }
    }
  }

  const mainOuterSubmit = (dt: any) => {
    handleDialogClose()
  }

  const onSetEdit = async (_id: number) => {
    const temp = data.map((el: any) =>
      el.id === _id ? { ...el, mode: 'edit' } : el
    )
    let item = await temp.find((f: any) => f.id === _id)
    const carePlanStatus = patientDetail?.medications?.care_plans?.find((f: { program: any }) => f.program === item?.program)?.plan_status || ''

    item = {
      ...item,
      'care_plan_status': carePlanStatus,
      totalRecomNeedsUpdate: (data?.filter((i: any) => i.is_recommended && i.needs_update)
        || [])?.length || 0
    }

    const current_stage = program_enrollments.filter((e: { program: any }) => e.program === item.program)[0].current_stage || ''

    const _tempCompConfig = item.is_recommended ?
      compsConfig.find((f: any) => Helper.checkGroup(currentRole, f?.role) && f.stage === current_stage)
      : compsConfig.find((f: { id: string }) => f.id === 'NOT_REC')

    const compsSet = _tempCompConfig?.tabs.map((t) => getInnerProps(t, item))

    const _actionsSet = _tempCompConfig?.actions.map((a: any) => {
      if (a.title === 'Cancel') {
        return { ...a, returnFun: handleDialogClose }
      } else if (a.title === 'Submit') {
        return { ...a, returnFun: mainOuterSubmit }
      } else {
        return { ...a }
      }
    })

    setConfigItem({
      ..._tempCompConfig,
      source_type: item.source_type,
      actions: _actionsSet,
      tabs: compsSet,
    })
    setCurrentItem(item)
    setOpen(true)
    // setData(temp)
  }

  useEffect(() => {
    if (Object.keys(selectedCarePlan).length > 0) {
      setData(
        data.map((el: any) =>
          el.id === selectedCarePlan.id
            ? {
              ...selectedCarePlan,
              care_plan: selectedCarePlan?.care_plan,
              programs: el.programs,
              program: el.program,
              mode: 'view',
            }
            : el
        )
      )
    }
  }, [selectedCarePlan])

  const updateMedication = async (_id: string, payload: any) => {
    try {
      return await patientService.putOtherMedication(patientDetail.id, _id, payload)
    } catch (err) {
      setIsOrderPlaced({ status: false, id: '' })
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
    }
  }

  const confirmMedicine = (
    _typeOut: any,
    medicinedata: any,
    _mode: any
  ) => {
    if (_typeOut === 'confirm') {
      const payload = {
        entity_ref: 'medication',
        entity_id: medicinedata.id,
        confirmed_by: patientInfo.id,
      }
      patientService.PostConfirmations(payload).then((_response: any) => {
        /* do nothing */
      }).catch((err: any) => {
        console.error(err)
      })
    }
  }

  const outerSubmit = (
    _id: number,
    _mode: string,
    _res: string,
    _date: Date | null
  ) => {
    const temp = data.find((f: any) => f.id === _id)
    if (_res === 'save') {
      const _payload = {
        communication_date: moment(new Date()).unix(),
        is_recommended: temp.is_recommended,
        source_type: temp.source_type,
      }
      updateMedication(_id.toString(), _payload).then((res: any) => {
        if (res) {
          setData(
            data.map((el: any) =>
              el.id === res.data.id
                ? {
                  ...res.data,
                  programs: temp.programs,
                  program: temp.program,
                  mode: 'view',
                }
                : el
            )
          )
          setProgramConfirmCount(
            temp.needs_update,
            res.data.needs_update,
            temp.programs
          )
        }
        setIsOrderPlaced({ status: false, id: '' })
      })
    } else if (_res === 'start') {
      setData(
        data.map((el: any) =>
          el.id === _id
            ? {
              ...el,
              mode: _mode,
              is_started: true,
              date_recorded: _date,
              status: 'Taking',
            }
            : el
        )
      )
    } else {
      setData(
        data.map((el: any) => (el.id === _id ? { ...el, mode: _mode } : el))
      )
    }
  }

  const onCarePlanRefClick = (prg: any, isOpen: boolean) => {
    setSelectedProgramObj(
      selectedProgramObj.map((e: any) => {
        return { ...e, isRef: (e.program === prg) ? isOpen : e.isRef }
      })
    )
    return
  }

  const onRefClick = (_id: any, _mode: string) => {
    setData(onSetView(_id, _mode, data))
  }

  const onRefSubmit = (_id: any, date: number, is_referred: boolean, referral_id: any) => {
    setData(onDataSubmit(_id, date, is_referred, referral_id, data))
  }

  const onDeleteMedicationClick = (_id: any, _mode: string) => {
    setData(onSetView(_id, _mode, data))
  }

  const onDeleteMedicationSubmit = (recId: any) => {
    setData(data.filter((e: { id: any }) => e.id !== recId))
    updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
  }
  const onCarePlanRefSubmit = (_id: any, date: number, is_referred: boolean, referral_id: any) => {
    setSelectedProgramObj(
      selectedProgramObj.map((e: any) => {
        if (e.care_plan_id === _id) {
          return { ...e, isRef: false, is_referred: is_referred, referral_id: referral_id }
        }
        else {
          return { ...e }
        }
      })
    )
  }

  const handleSave = (
    _values: any,
    _mode: any,
    _typeOut: any,
    medicinedata: any,
    _dosageInfo: any,
    response: any
  ) => {
    if (_typeOut === 'confirm') {
      setCount(
        data.find((f: { id: string }) => f.id === medicinedata.id)
          ?.needs_confirmation,
        false
      )
    } else {
      setCount(
        data.find((f: { id: string }) => f.id === medicinedata.id)
          ?.needs_confirmation,
        true
      )
    }

    const tempObj = data.find((f: { id: string }) => f.id == medicinedata.id)

    if (tempObj.source_type === 'imported') {
      setProgramConfirmCount(tempObj?.needs_confirmation, _typeOut == 'confirm' ? false : true, tempObj?.programs)
    }

    if (_typeOut === 'confirm') {
      confirmMedicine(_typeOut, medicinedata, _mode)

      setData(
        data.map((el: any) =>
          el.id === medicinedata.id
            ? {
              ...response.data,
              mode: _mode,
              programs: el.programs,
              program: el.program,
              needs_confirmation: false,
              recent_confirmation: {
                confirmed_on: response.data.date_recorded,
              },
            }
            : el
        )
      )
    } else {
      setData(
        data.map((el: any) =>
          el.id === medicinedata.id
            ? {
              ...response.data,
              mode: _mode,
              programs: el.programs,
              program: el.program,
              needs_confirmation: response.data.needs_confirmation
                ? response.data.needs_confirmation
                : false,
              confirmed_on: '',
              date_recorded: response.data.date_recorded,
            }
            : el
        )
      )
    }
  }

  const getEvalDate = (sl: string) => {
    let currPlanObj = data.filter((m: any) => m.program === sl && m?.care_plan)[0]
    return (currPlanObj?.care_plan)
      ? currPlanObj?.care_plan?.last_evaluation_date || ''
      : patientDetail?.medications?.care_plans?.find((f: { program: any }) => f.program === sl)?.last_evaluation_date || ''

  }
  const lastEvalDate = (sl: string) => {
    const prgEvalDate = getEvalDate(sl)
    return (prgEvalDate)
      ? <List className={classes.criteriaList} style={{ paddingLeft: '0.85rem' }}>
        <ListItem className={classes.puList}>
          <Typography variant='body2' className={classes.evalBoxVal}>
            Last evaluation: {moment.unix(prgEvalDate).format('MMM DD, YYYY hh:mm a')}
          </Typography>
        </ListItem>
      </List>
      : ''
  }

  const pendingMedActions = (item: any) => {
    let currInstObj = [] as any
    if (item.is_communicated === false) {
      currInstObj.push('Patient communication', 'Provider approval')
    } else {
      if (item.approval_status !== 'Approved') {
        currInstObj.push(`Provider approval`)
      }
      if (
        Helper.checkGroup(environment.okta.groups, currentRole) &&
        item.approval_status === 'Approved' &&
        (configItem.stage === 'med_initiation' ||
          (item?.titration_status?.includes('prior dose')
            || item?.titration_status?.includes('lowest dose')
            || item?.titration_status?.includes('downtitrate')
          )
        ) && item.intake_started_on === null
      ) {
        currInstObj.push(`Start date if patient has started taking med.`)
      }
      if (
        Helper.checkGroup(environment.okta.groups, currentRole) &&
        item?.titration_status?.includes('discontinue') &&
        item.intake_stopped_on === null
      ) {
        currInstObj.push(`Stop date if patient stopped taking med`)
      }
    }
    if (item.is_recommended && !item.is_order_submitted) {
      const isAllCaptureBaselineCount = (data?.filter((i: any) => i.is_recommended
        && i.is_communicated
        && i.approval_status !== 'Approved'
        && i.is_ordered === false
        && ['data_intake', 'med_baseline'].includes(getStageString(i.programs[0]))
        && !(i.titration_status?.includes('discontinue') && i.needs_update)
      ) || [])?.length || 0

      const approvedCount = (data?.filter((i: any) => i.is_recommended
        && i.is_communicated
        && i.approval_status === 'Approved'
        && i.program === item.program
        && i.is_ordered === false
        && ['data_intake', 'med_baseline'].includes(getStageString(i.programs[0]))
        && !(i.titration_status?.includes('discontinue') && i.needs_update)
      ) || [])?.length || 0

      const prgPendingCount = getPrgPendingCount(item.programs[0])

      if (item.is_baselined === true && item.is_ordered === false) {
        currInstObj.push(`Order Placed`)
      }
      else if (item.is_communicated && item.approval_status === 'Approved'
        && item.is_ordered === false
        && ['data_intake', 'med_baseline'].includes(getStageString(item.programs[0]))
        && ((prgPendingCount === approvedCount && isAllCaptureBaselineCount === 0
        ))
      ) {
        currInstObj.push(`Capture Baseline`)
      }
    }

    return currInstObj?.length > 0 && <>
      <Typography variant="body2" className={classes.evalBoxVal}>
        Pending actions:
      </Typography>
      <List className={classes.confirmCardContent}>
        {currInstObj?.map((d: any) => {
          return <ListItem className={classes.puList} key={`action-${d}`}>
            <Typography variant='body2' component="span" className={classes.evalBoxVal}>
              {d}
            </Typography>
          </ListItem>
        })
        }
      </List>
    </>
  }

  const printInstructions = (sl: string) => {
    let currPlanObj = data.filter((m: any) => m.program === sl && m?.care_plan)[0]
    const currInstObj = (currPlanObj?.care_plan)
      ? currPlanObj?.care_plan?.instructions || []
      : instructions.filter((m: any) => m.program === sl)[0]?.instructions || []

    if (Object.keys(currInstObj)?.length === 1 && currInstObj[0].includes('Recommendation pending'))
      return ''

    return Object.keys(currInstObj)?.length > 0 && <List sx={{ padding: '0px 0px 5px 0.85rem' }}>
      {Object.keys(currInstObj)?.map((d: any) => {
        if (currInstObj[d].startsWith('[')) {
          try {
            let currInst = currInstObj[d]
            currInst = eval(currInst);
            let actionList = currInst !== undefined && currInst && Object.keys(currInst)?.map((i: any, index: any) => {
              const { action, color, display_name } = currInst[i]
              // Map dark colors to lighter variants for dark mode visibility
              const lightColor = color === '#007200' ? 'var(--color-success)'
                : color === '#ff0f39' ? 'var(--color-error)'
                  : color === '#e69138' ? 'var(--color-warning)'
                    : color || 'var(--color-text-secondary)'
              return <>
                <Typography component="span" className={classes.instructionVal} sx={{ '&&': { color: `${lightColor}` } }}>
                  {display_name && display_name !== 'None' ? display_name : action}
                </Typography>
                <Typography component="span" className={classes.evalBoxVal} sx={{ paddingLeft: '0.01rem' }}>
                  {`${(index + 1) < currInst?.length ? ', ' : ''}`}
                </Typography>
              </>
            })
            return <ListItem className={classes.puList} key={`inst-${d}`}>
              <Typography variant='body2' className={classes.evalBoxVal}>Plan: {actionList}</Typography>
            </ListItem>
          }
          catch {
            return <ListItem className={classes.puList} key={`inst-${d}`}>
              <Typography variant='body2' className={classes.evalBoxVal}>Plan</Typography>
            </ListItem>
          }
        }
        else if (!currInstObj[d].includes('Recommendation pending')) {
          return <ListItem className={classes.puList} key={`inst-${d}`}>
            <Typography variant='body2' component="span" className={classes.evalBoxVal}>
              {currInstObj[d]}
            </Typography>
          </ListItem>
        }
      })
      }
    </List>
  }

  const handleProviderRecom = () => { }

  const getCardOutline = (sl: string) => {
    const rawColor = program_enrollments?.find((f: { program: string }) => f.program === sl)?.color_code || '#F06423'
    const color = adjustColorForDarkMode(rawColor, isDarkMode) || '#F06423'
    if (Object.keys(selectedProgramObj)?.length > 0 &&
      (selectedProgramObj.filter((e: any) => e.program === sl && e.is_referred))?.length > 0) {
      return {
        border: '0.15rem solid #DB2323 !important',
        backgroundColor: isDarkMode ? '#3d2020 !important' : '#f8ebeb !important',
        borderRadius: '4px'
      }
    }
    else {
      return { border: `2px solid ${color} !important` }
    }
  }

  const getStageString = (sl: string) => program_enrollments?.find((f: { program: any }) => f.program === sl)?.current_stage

  const isRecomMed = (prg: any) => data?.some((f: { is_recommended: boolean, program: any, titration_status: any }) => (!['discontinue', 'discontinue, restrict', 'discontinue, and initiate']?.includes(f.titration_status)) && f.is_recommended && f.program === prg) || false

  // Check if a program has titrating medications (initiate or uptitrate) for program-level evaluation
  const hasTitratingMedications = (prg: string) => data?.some((f: { is_recommended: boolean, program: any, titration_status: string }) =>
    f.is_recommended && f.program === prg &&
    (f.titration_status?.includes('initiate') || f.titration_status?.includes('uptitrate'))
  ) || false

  // Check if program is in evaluation stage for showing Run Program Evaluation button
  const isInEvaluationStage = (prg: string) => {
    const stage = getStageString(prg)
    return ['med_evaluation', 'early_tox_check', 'maintenance_surveillance'].includes(stage)
  }

  // Open the program evaluation dialog
  const openProgramEvaluationDialog = (prg: string) => {
    const carePlan = patientDetail?.medications?.care_plans?.find((f: { program: any }) => f.program === prg)
    const enrollment = program_enrollments.find((p: any) => p.program === prg)
    if (carePlan?.care_plan_id) {
      setProgramEvalDialog({
        open: true,
        program: prg,
        programDisplayName: enrollment?.program_display_name || prg,
        carePlanId: carePlan.care_plan_id
      })
    }
  }

  // Close the program evaluation dialog and refresh data
  const closeProgramEvaluationDialog = () => {
    setProgramEvalDialog({ open: false, program: '', programDisplayName: '', carePlanId: 0 })
  }

  const handleProgramEvalComplete = (actionLabel?: string) => {
    if (actionLabel && programEvalDialog.program) {
      setLastEvalAction(prev => ({ ...prev, [programEvalDialog.program]: actionLabel }))
    }
    updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
  }

  const getMedMoreDetails = (pId: string, recId: string, medName: string, code: any) => {
    setLoading(true)
    setMedTitle(`${medName} [${code}]`)

    patientService.getMedMoreDetails(pId, recId)
      .then((response: any) => {
        setMedMoreDetails(response.data?.fhir_json);
        setLoading(false)
      }).catch((err: any) => {
        setLoading(false)
        console.error(err);
      });
    setOpenFhirJson(true)
  }

  const handleFhirJsonClose = () => setOpenFhirJson(false)

  const getCarePlanStatus = (sl: any) => {
    let currPlanMedObj = data.filter((m: any) => m.program === sl)[0]
    const carePlanObj = (currPlanMedObj?.care_plan)
      ? currPlanMedObj?.care_plan
      : patientDetail?.medications?.care_plans?.find((f: { program: any }) => f.program === sl) || {}

    const carePlanName = carePlanObj?.plan_display_name || carePlanObj?.plan_name || ''
    let statusLabel = carePlanObj?.plan_status || ''
    if (statusLabel === 'partially_effective') statusLabel = 'intensify'
    else if (statusLabel === 'toxicity_management') statusLabel = 'toxicity monitoring'
    else if (statusLabel === 'ineffective') statusLabel = 'ineffective plan'
    else if (statusLabel === 'effective') statusLabel = 'effective plan'
    return ` (${statusLabel}${carePlanName !== undefined && carePlanName !== '' ? ` - ${carePlanName}` : ''})`
  }

  const getTreatmentPrg = () => {
    let treatmentPrograms = selectedProgram
    selectedProgram?.map((i: any) => {
      if (!program_enrollments?.some((m: any) => m.program === i && m.program_type !== 'evaluation')) {
        treatmentPrograms = treatmentPrograms.filter((f: any) => f !== i)
      }
    })
    return treatmentPrograms
  }

  const getSelectedPrg = () => {
    getTreatmentPrg()
    return (patientInfo?.isActionItems && patientInfo?.actionProgram)
      ? [patientInfo?.actionProgram]
      : getTreatmentPrg()
  }

  const isMedExists = () => patientInfo?.isActionItems === true && patientInfo?.actionProgram
    ? data?.length > 0
    : true

  const getCarePlanDetials = (sl: any) => {
    const carePlanObj = selectedProgramObj.find((f: { program: any }) => f.program === sl)
    return {
      id: carePlanObj?.care_plan_id,
      is_referred: carePlanObj?.is_referred || false,
      referral_id: carePlanObj?.referral_id
    }
  }

  const getCarePlanTitle = (sl: any) => {
    const prgDisplayName = program_enrollments.find((p: any) => p.program === sl)?.program_display_name || ''
    return `${prgDisplayName}  ${getCarePlanStatus(sl)}`
  }

  const isDependentOverridden = (medInfo: any) => {
    return (JSON.stringify(medInfo?.evaluation_results))?.includes(`"dependent_has_override":true`)
      || (JSON.stringify(medInfo?.c_rationale))?.includes(`"dependent_has_override":true`)
  }

  // Profile render
  profiler.start(`ProgramMedicationsComponent render #${renderCountRef.current}`)

  // Use useEffect to mark end of render (runs after DOM update)
  useEffect(() => {
    profiler.end(`ProgramMedicationsComponent render #${renderCountRef.current}`)
  })

  return (
    <>
      <Grid container direction="row">
        {isMedExists() && getSelectedPrg()?.map((sl: any, i: any) => (
          <Grid container key={`prg-med-${i}`} direction="column">
            {Helper.isTreatmentPrg(patientDetail?.program_enrollments || [])
              ? <Box mb={0.4} p={0} className={classes.customCard}>
                <Card variant="outlined" sx={getCardOutline(sl)}>
                  <CardContent sx={{ padding: '2px 4px 0px 4px !important' }}>
                    <Grid container direction="column" justifyContent="flex-start" alignItems={'center'}>
                      <Grid item className={classes.fullWidth}>
                        <Stack alignItems={'flex-start'} spacing={0.2}>
                          <Grid container direction="row" alignItems={'center'}>
                            <Grid item style={{ width: '97%' }}>
                              <Typography variant="subtitle2" className={classes.programTargetHeader} sx={{ color: 'var(--color-primary) !important' }}>
                                {getCarePlanTitle(sl)}
                              </Typography>
                            </Grid>
                            <Grid item style={{ width: '3%', height: '12px' }}>
                              {!patientInfo?.isActionItems &&
                                <LongMenu
                                  patientId={patientId}
                                  isDisabled={stageChange === true || patientDetailStatus === 'loading'}
                                  title={getCarePlanTitle(sl)}
                                  currPrg={program_enrollments.find((p: any) => p.program === sl)?.program}
                                  totalRecomNeedsUpdate={data?.filter((i: any) => i.is_recommended && i.needs_update)?.length}
                                  prgStatus={Helper.isPrgInActive(patientDetail?.program_enrollments, [sl])}
                                  currStage={getStageString(sl)}
                                  onRefClick={() => onCarePlanRefClick(sl, true)}
                                  isDiscontinue={data?.some((f: any) => f.is_recommended && f.program === sl && f.titration_status?.includes('discontinue')) || false}
                                  isRecomMed={data?.some((f: { is_recommended: boolean, program: any }) => f.is_recommended && f.program === sl) || false}
                                  careplanId={patientDetail?.medications?.care_plans?.find((f: { program: any }) => f.program === sl)?.care_plan_id || -1}
                                  onProviderSubmit={handleProviderRecom}
                                />
                              }
                            </Grid>
                          </Grid>
                          {!data?.some((f: { is_recommended: boolean, program: any }) => f.is_recommended && f.program === sl)
                            && getPrgPendingCount(sl) === 0 &&
                            <Grid item className={classes.fullWidth}>
                              <Typography variant="body2" className={classes.evalBoxVal}>
                                Action: Generate care plan
                              </Typography>
                            </Grid>
                          }
                          <Grid item className={classes.fullWidth}>
                            <Typography variant="body2" className={classes.desc}>
                              {lastEvalDate(sl)}
                            </Typography>
                          </Grid>
                          <Grid item className={classes.fullWidth}>
                            <Typography variant="body2" className={classes.desc}>
                              {printInstructions(sl)}
                            </Typography>
                          </Grid>
                          {lastEvalAction[sl] && (
                            <Grid item className={classes.fullWidth}>
                              <Typography variant="body2" sx={{ color: 'var(--color-warning)', fontWeight: 500, fontSize: '0.75rem' }}>
                                Last action: {lastEvalAction[sl]}
                              </Typography>
                            </Grid>
                          )}
                          {/* Program-level evaluation button - shows in evaluation stages with titrating meds */}
                          {isInEvaluationStage(sl) && hasTitratingMedications(sl) && !patientInfo?.isActionItems && (
                            <Grid item className={classes.fullWidth}>
                              <Button
                                variant="outlined"
                                className={classes.diagnosisPlanBtn}
                                onClick={() => openProgramEvaluationDialog(sl)}
                                disabled={(stageChange === true ||
                                  patientDetailStatus === 'loading' ||
                                    getPrgPendingCount(sl) > 0
                                )}
                                sx={{ marginTop: '0rem !important', marginBottom: '0.15rem !important' }}
                              >
                                <Typography className={classes.diagnosisPlanBtnLabel}
                                  variant="body2"
                                  color={(stageChange === true || patientDetailStatus === 'loading' ||
                                    getPrgPendingCount(sl) > 0
                                    ) ? 'grey' : ''
                                  }
                                >
                                  <small>Run program evaluation</small>
                                </Typography>
                              </Button>
                            </Grid>
                          )}
                        </Stack>
                      </Grid>

                      {data?.filter((f: { is_recommended: boolean }) =>
                        f.is_recommended === true).map((d: any) => (
                          <React.Fragment key={`prg-med-recom-${d.id} `}>
                            {d.mode === 'view' && d.program === sl && (
                              <>
                                {patientInfo?.isActionItems === true && patientInfo?.actionProgram &&
                                  pendingMedActions(d)
                                }
                                {(() => {
                                  const medsCount = data?.filter((i: any) => i.is_recommended && i.needs_update && i.program === sl)?.length || 0
                                  const targetsCount = programTargets?.some((t: any) => t.program === sl && !((t.overridden_programs || []).includes(sl)) && (t.needs_update || t.needs_confirmation)) ? 1 : 0
                                  const pending = getPrgPendingCount(sl)
                                  console.log('STALE pendingCount=' + pending + ' medsNeedsUpdate=' + medsCount + ' targetsCount=' + targetsCount)
                                  console.log('STALE measurementKeys=' + (patientDetail?.measurements ? Object.keys(patientDetail.measurements).join(',') : 'NONE'))
                                  console.log('STALE programTargets count=' + programTargets?.length + ' first=' + JSON.stringify(programTargets?.[0] ? { program: programTargets[0].program, needs_update: programTargets[0].needs_update, needs_confirmation: programTargets[0].needs_confirmation, name: programTargets[0].name } : null))
                                  console.log('STALE allMeds=' + JSON.stringify(data?.filter((i: any) => i.program === sl)?.map((i: any) => ({ name: i.name, is_rec: i.is_recommended, nu: i.needs_update, nc: i.needs_confirmation }))))
                                  return null
                                })()}
                                <CardLayoutComponent
                                  id={d.id}
                                  name={d?.name}
                                  input={{
                                    isDependentOverridden: isDependentOverridden(d),
                                    pendingCount: getPrgPendingCount(sl),
                                    recommendation_date: d?.recommendation_date,
                                    totalRecomNeedsUpdate: (data?.filter((i: any) => i.is_recommended && i.needs_update && i.program === sl)?.length || 0)
                                      + (programTargets?.some((t: any) => t.program === sl && !((t.overridden_programs || []).includes(sl)) && (t.needs_update || t.needs_confirmation)) ? 1 : 0),
                                    isDisabled: (stageChange === true || patientDetailStatus === 'loading'),
                                    approval_status: Boolean(d.approval_status == 'Approved'),
                                    stage: getStageString(sl)
                                  }}
                                  leftHeader={checkCondition(d.titration_status === 'initiate', Helper.getRXnorm(d.pu_med_name, d.rxnorm), Helper.getRXnorm(d.pu_order?.name, d.taking_med_rxnorm))}
                                  subHeader={formatSubHeader(d)}
                                  leftHeader1={checkCondition((d.titration_status)?.includes('discontinue') || (d.titration_status)?.includes('initiate_current'), '', checkCondition(d.intake_started_on === null && d.titration_status !== 'initiate' && d.pu_order?.name !== d.pu_med_name, `${checkCondition(d.pu_med_name !== null, Helper.getRXnorm(d.pu_med_name, d.rxnorm), Helper.getRXnorm(d.name, d.rxnorm))} `, ``))}
                                  subHeader1={checkCondition((d.titration_status)?.includes('discontinue') || (d.titration_status)?.includes('initiate_current'), '', checkCondition(d.titration_status !== 'initiate' && d.intake_started_on === null, formatSubHeader1(d), ''))}
                                  rightHeader={formatRightHeader(d)}
                                  bottomText={formatConfirmDate(d)}
                                  modified_by_name={d.modified_by_name}
                                  modified_on={d.modified_on}
                                  source={`${d?.source_type} - ${d?.source_system ? d?.source_system : 'ATMAN'}`}
                                  needs_confirmation={
                                    d.source_type === 'imported'
                                      ? d.needs_confirmation || undefined
                                      : undefined
                                  }
                                  needs_update={d.needs_update || undefined}
                                  icons={(patientInfo?.isActionItems === true) ? [] : setIcons(d)}
                                  is_recommended={d.is_recommended}
                                  programs={d.programs}
                                  onRefClick={() => onRefClick(d.id, 'ref')}
                                  onMedDetailsClick={() => getMedMoreDetails(patientDetail.id, d.id, d.pu_med_name ? d.pu_med_name : d.name, d?.rxnorm)}
                                  onDeleteMedicationClick={() => onDeleteMedicationClick(d.id, 'deleteMed')}
                                  is_referred={d.is_referred}
                                  is_overridden={d.is_overridden || undefined}
                                  entityRef="medication"
                                  patientId={patientId}
                                  is_care_plan_non_recc={
                                    !(d.is_recommended || d.is_referred)
                                  }
                                  category={'medications'}
                                  titration_status={d.titration_status}
                                  recommendation_source={d?.recommendation_source}
                                />
                                {selectedNotes.includes(d.id) && (
                                  <NotesComponent
                                    entityId={d.id}
                                    entityRef={'medication'}
                                    onCancel={onNotesClick}
                                  />
                                )}
                              </>
                            )}
                            {d.mode === 'ref' && d.program === sl && (
                              <ReferralComponent
                                entityRef="medication"
                                input={d}
                                patientId={patientId}
                                leftHeader={Helper.getRXnorm(d.name, d.rxnorm)}
                                rightHeader={formatRightHeader(d)}
                                subHeader={formatSubHeader(d)}
                                programs={d.programs}
                                closeRef={() => onRefClick(d.id, 'view')}
                                onRefSubmit={onRefSubmit}
                              />
                            )}
                            {d.mode === 'deleteMed' && d.program === sl && (
                              <DeleteMedComponent
                                entityRef='medication'
                                input={d}
                                patientId={patientDetail.id}
                                leftHeader={Helper.getRXnorm(d.name, d?.rxnorm)}
                                rightHeader={d.intake_regularity?.charAt(0).toUpperCase() +
                                  d.intake_regularity?.slice(1) || ''}
                                subHeader={formatSubHeader(d)}
                                programs={d.programs}
                                closeDeleteMed={() => onDeleteMedicationClick(d.id, 'view')}
                                onDeleteMedicationSubmit={onDeleteMedicationSubmit}
                              />
                            )}
                            {d.mode === 'edit' &&
                              d.is_recommended &&
                              d.program === sl && (
                                <>
                                  <ScheduleCarePlanComponent data={d} outer={outerSubmit} />
                                </>
                              )}
                            {d.mode === 'edit' &&
                              !d.is_recommended &&
                              d.program === sl && (
                                <>
                                  <ProgramSubmedicationsComponent
                                    input={d}
                                    outer={outerSubmit}
                                    selectedProgram={sl}
                                    uiResource={uiResource}
                                    patientInfo={patientInfo}
                                    handleP={handleClose}
                                    passData={handleSave}
                                    changeOption={onBtnChange}
                                    currentOption={currentOption}
                                  />
                                </>
                              )}
                          </React.Fragment>
                        ))}
                      <Box pt={0.25} className={classes.fullWidth}>
                        <OtherMedicationsComponent
                          patientInfo={{
                            ...patientInfo,
                            'is_recommended': isRecomMed(sl),
                            'isPrgMed': true,
                            'isActionItems': patientInfo?.isActionItems,
                            'actionProgram': patientInfo?.actionProgram
                          }}
                          currentProgram={patientInfo?.isActionItems ? patientInfo?.actionProgram : sl}
                        />
                      </Box>
                      {Object.keys(selectedProgramObj)?.length > 0 &&
                        (selectedProgramObj.filter((e: any) => e.program === sl && e.isRef))?.length > 0 && (
                          <ReferralComponent
                            entityRef="care_plan"
                            isCarePlan={true}
                            input={getCarePlanDetials(sl)}
                            patientId={patientId}
                            leftHeader={getCarePlanTitle(sl)}
                            rightHeader={''}
                            subHeader={''}
                            programs={[sl]}
                            closeRef={() => onCarePlanRefClick(sl, false)}
                            onRefSubmit={onCarePlanRefSubmit}
                          />
                        )}
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
              : <EmptyCardComponent />
            }
          </Grid >
        ))}
      </Grid >

      {open && (
        <BaseDialogModalComponent
          open={open}
          currentRole={currentRole}
          item={{ ...currentItem, 'lastEvalDate': getEvalDate(currentItem.program) }}
          configItem={configItem}
          handleDialogClose={handleDialogClose}
          patientId={patientId}
        />
      )}
      {openFhirJson && (
          <MedDetailsComponent
            openFhirJson={openFhirJson}
            medTitle={medTitle}
            handleFhirJsonClose={handleFhirJsonClose}
            medMoreDetails={medMoreDetails} />
        )
      }
      {/* Program-level evaluation dialog */}
      {programEvalDialog.open && (
          <ProgramEvaluationDialogComponent
            open={programEvalDialog.open}
            onClose={closeProgramEvaluationDialog}
            patientId={patientId}
            programName={programEvalDialog.program}
            programDisplayName={programEvalDialog.programDisplayName}
            carePlanId={programEvalDialog.carePlanId}
            onApprovalComplete={handleProgramEvalComplete}
            onEvaluationRun={handleProgramEvalComplete}  // Refresh to show recommendations after eval
          />
        )
      }
    </>
  )
}

export default withStyles(stylesheet)(ProgramMedicationsComponent)